/** A delivered notification, as returned by the inbox API and pushed live over
 *  SignalR. Mirrors the backend `NotificationDto` (camelCase on the wire). */
export interface Notification {
  id: number;
  type: string;
  title: string;
  body: string;
  /** Frontend route/tab to open on click (e.g. "approvals"). */
  deepLink?: string | null;
  /** Type-specific payload as raw JSON; parse per `type`. */
  data?: string | null;
  read: boolean;
  createdAtUtc: string;
}

/** Inbox API response envelope (`ApiResponse<InboxResponse>.data`). */
export interface InboxResponse {
  items: Notification[];
  unreadCount: number;
}

/** `type` of a message sent by a skill's Notify node with channel = aiva_chat. */
export const SKILL_MESSAGE_TYPE = 'skill_message';

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

/** Parsed `data` payload of a `skill_message` notification. */
export interface SkillMessageData {
  skillName: string;
  priority: NotificationPriority;
}

const PRIORITIES: ReadonlySet<string> = new Set(['low', 'normal', 'high', 'urgent']);

/**
 * Parse a `skill_message` notification's `data`. Returns null for any other
 * type, or when `data` is missing or not valid JSON — the raw payload comes from
 * the backend as an opaque string, so it is never trusted to be well-formed.
 */
export function parseSkillMessageData(n: Notification): SkillMessageData | null {
  if (n.type !== SKILL_MESSAGE_TYPE || !n.data) {
    return null;
  }
  let raw: unknown;
  try {
    raw = JSON.parse(n.data);
  } catch {
    return null;
  }
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const skillName = typeof record.skillName === 'string' ? record.skillName.trim() : '';
  const priority =
    typeof record.priority === 'string' && PRIORITIES.has(record.priority)
      ? (record.priority as NotificationPriority)
      : 'normal';
  return { skillName, priority };
}
